import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { listSuperAdminTenants } from "../../services/superadmin-tenants";
import DataTable from "../../components/table/DataTable";
import StatusBadge from "../../components/table/StatusBadge";
import ActionGroup from "../../components/table/ActionGroup";
import TablePaginationFooter from "../../components/table/TablePaginationFooter";
import { normalizeSpringPagination } from "../../helpers/pagination";

const TenantList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tenants, setTenants] = useState([]);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState(searchParams.get("search") ?? "");
  const [active, setActive] = useState(searchParams.get("active") ?? "");

  const currentPage = Number(searchParams.get("page") ?? 0);

  useEffect(() => {
    const loadTenants = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await listSuperAdminTenants({
          search: searchParams.get("search") ?? "",
          active: searchParams.get("active") ?? "",
          page: currentPage,
          size: 20,
        });
        setTenants(response.content ?? []);
        setPage(response);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    loadTenants();
  }, [currentPage, searchParams]);

  const applyFilters = () => {
    const next = {};
    if (search.trim() !== "") {
      next.search = search.trim();
    }
    if (active !== "") {
      next.active = active;
    }
    next.page = "0";
    setSearchParams(next);
  };

  const goToPage = (nextPage) => {
    const next = Object.fromEntries(searchParams.entries());
    next.page = `${nextPage}`;
    setSearchParams(next);
  };

  const tenantColumns = [
    {
      key: "code",
      label: "Code",
      width: "220px",
      render: (tenant) => <span className="font-semibold">{tenant.code}</span>,
    },
    { key: "name", label: "Name" },
    {
      key: "status",
      label: "Status",
      width: "160px",
      render: (tenant) => <StatusBadge active={tenant.active} />,
    },
    {
      key: "actions",
      label: "Actions",
      align: "right",
      width: "140px",
      render: (tenant) => (
        <ActionGroup>
          <Link
            to={`/su/admin/tenants/${tenant.code}`}
            title="Details"
            className="font-semibold text-primary underline hover:text-primary-dark"
          >
            Details
          </Link>
        </ActionGroup>
      ),
    },
  ];

  const pagination = normalizeSpringPagination(page, tenants.length);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between mb-6">
        <div>
          <h1 className="uppercase text-xl font-bold text-slate-700">
            Tenants
          </h1>
          <p className="text-sm text-slate-500">
            Manage tenant workspaces and configuration.
          </p>
        </div>
        <Link
          to="/su/admin/tenants/create"
          className="bg-primary text-white font-bold uppercase text-sm px-6 py-2 rounded shadow hover:bg-primary-dark"
        >
          Create Tenant
        </Link>
      </div>

      <div className="bg-white rounded shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_120px] gap-3">
          <input
            type="text"
            className="border px-3 py-2 rounded text-sm"
            placeholder="Search code or name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className="border px-3 py-2 rounded text-sm"
            value={active}
            onChange={(e) => setActive(e.target.value)}
          >
            <option value="">All statuses</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <button
            type="button"
            className="bg-primary-light text-white uppercase text-sm font-bold rounded"
            onClick={applyFilters}
          >
            Search
          </button>
        </div>
      </div>

      {error ? (
        <div className="bg-errorBg text-errorText text-sm p-4 mb-6 border-errorBorder border-[0.5px] rounded-sm">
          {error}
        </div>
      ) : null}

      <DataTable
        columns={tenantColumns}
        rows={tenants}
        rowKey="code"
        loading={loading}
        minWidth="720px"
        emptyMessage="No tenants found."
        footer={
          pagination ? (
            <TablePaginationFooter {...pagination} onPage={goToPage} />
          ) : null
        }
      />
    </div>
  );
};

export default TenantList;
